import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Link } from "react-router-dom";
import { FolderKanban, FileText, LifeBuoy, Sparkles, Globe2, Server, LineChart } from "lucide-react";
import Invoices from "@/pages/Invoices";
import Support from "@/pages/Support";
import AIAssistant from "@/pages/AIAssistant";

const PortalCard = ({ icon: Icon, title, desc, to, testid }) => (
  <Link to={to} data-testid={testid} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 hover:border-blue-500/50 transition-colors group">
    <div className="w-10 h-10 rounded-md bg-blue-500/10 text-blue-400 grid place-items-center"><Icon className="w-5 h-5" /></div>
    <div className="mt-3 font-display text-sm font-semibold text-zinc-100">{title}</div>
    <div className="mt-1 text-xs text-zinc-500">{desc}</div>
  </Link>
);

export function ClientPortalHome() {
  const { user } = useAuth();
  const { t } = useLang();
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  useEffect(() => {
    Promise.all([
      api.get("/projects").catch(() => ({ data: [] })),
      api.get("/invoices").catch(() => ({ data: [] })),
    ]).then(([p, i]) => { setProjects(p.data); setInvoices(i.data); });
  }, []);
  return (
    <DashboardLayout title={t("portal.title")} subtitle={`${t("portal.welcome")}, ${user?.name || ""}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="portal-home">
        <PortalCard testid="portal-tile-projects" icon={FolderKanban} title={t("portal.projects")} desc={`${projects.length} active`} to="/portal/projects" />
        <PortalCard testid="portal-tile-invoices" icon={FileText} title={t("portal.invoices")} desc={`${invoices.filter(i => i.status !== "paid").length} pending`} to="/portal/invoices" />
        <PortalCard testid="portal-tile-support" icon={LifeBuoy} title={t("portal.support")} desc="Contact us anytime" to="/portal/support" />
        <PortalCard testid="portal-tile-ai" icon={Sparkles} title={t("portal.ai")} desc="Chat with AI" to="/portal/ai" />
        <PortalCard testid="portal-tile-domains" icon={Globe2} title={t("portal.domains")} desc="Manage" to="/portal/domains" />
        <PortalCard testid="portal-tile-hosting" icon={Server} title={t("portal.hosting")} desc="Status" to="/portal/hosting" />
        <PortalCard testid="portal-tile-reports" icon={LineChart} title={t("portal.reports")} desc="Reports & analytics" to="/portal/reports" />
      </div>
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="portal-recent">
        <h3 className="font-display text-sm font-semibold text-zinc-100 mb-3">Recent projects</h3>
        <div className="divide-y divide-zinc-800">
          {projects.slice(0, 5).map((p) => (
            <div key={p.project_id} className="flex items-center justify-between py-3">
              <div>
                <div className="text-sm text-zinc-100">{p.name}</div>
                <div className="text-xs text-zinc-500">{p.kind}</div>
              </div>
              <span className="text-[10px] uppercase tracking-widest text-blue-400">{p.status}</span>
            </div>
          ))}
          {projects.length === 0 && <div className="text-xs text-zinc-500 py-6 text-center">No data</div>}
        </div>
      </div>
    </DashboardLayout>
  );
}

export function ClientPortalProjects() {
  const { t } = useLang();
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get("/projects").then(({ data }) => setProjects(data)); }, []);
  return (
    <DashboardLayout title={t("portal.projects")} subtitle="Track progress on all your projects">
      <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="portal-projects-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800 bg-zinc-900/40">
              <th className="p-3">Project</th><th className="p-3">Kind</th><th className="p-3">Start</th><th className="p-3">End</th><th className="p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.project_id} className="border-b border-zinc-800 hover:bg-zinc-900/40">
                <td className="p-3 text-zinc-100">{p.name}</td>
                <td className="p-3 text-zinc-400 text-xs">{p.kind}</td>
                <td className="p-3 text-zinc-500 text-xs">{p.start_date || "—"}</td>
                <td className="p-3 text-zinc-500 text-xs">{p.end_date || "—"}</td>
                <td className="p-3"><span className="text-xs text-blue-400">{p.status}</span></td>
              </tr>
            ))}
            {projects.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-zinc-500">No data</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

export function ClientPortalDomains() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/records/domains").then(({ data }) => setItems(data)); }, []);
  return (
    <DashboardLayout title="My domains" subtitle="Renewals and status">
      <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="portal-domains-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-zinc-500 border-b border-zinc-800 bg-zinc-900/40"><th className="p-3">Domain</th><th className="p-3">Registrar</th><th className="p-3">Expires</th><th className="p-3">Status</th></tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.record_id} className="border-b border-zinc-800 hover:bg-zinc-900/40">
                <td className="p-3 text-zinc-100">{(r.data||{}).name}</td>
                <td className="p-3 text-zinc-400 text-xs">{(r.data||{}).registrar || "—"}</td>
                <td className="p-3 text-zinc-500 text-xs">{(r.data||{}).expires_at || "—"}</td>
                <td className="p-3 text-blue-400 text-xs">{(r.data||{}).status || "—"}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-zinc-500">No data</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
export function ClientPortalHosting() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/records/hosting").then(({ data }) => setItems(data)); }, []);
  return (
    <DashboardLayout title="My hosting" subtitle="Servers and environments">
      <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="portal-hosting-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-zinc-500 border-b border-zinc-800 bg-zinc-900/40"><th className="p-3">Name</th><th className="p-3">Provider</th><th className="p-3">Region</th><th className="p-3">Status</th></tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.record_id} className="border-b border-zinc-800 hover:bg-zinc-900/40">
                <td className="p-3 text-zinc-100">{(r.data||{}).name}</td>
                <td className="p-3 text-zinc-400 text-xs">{(r.data||{}).provider || "—"}</td>
                <td className="p-3 text-zinc-500 text-xs">{(r.data||{}).region || "—"}</td>
                <td className="p-3 text-blue-400 text-xs">{(r.data||{}).status || "—"}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-zinc-500">No data</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
export function ClientPortalFiles() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/records/files").then(({ data }) => setItems(data)); }, []);
  return (
    <DashboardLayout title="Files" subtitle="Documents shared with you">
      <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="portal-files-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-zinc-500 border-b border-zinc-800 bg-zinc-900/40"><th className="p-3">File</th><th className="p-3">Type</th><th className="p-3">Link</th></tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.record_id} className="border-b border-zinc-800 hover:bg-zinc-900/40">
                <td className="p-3 text-zinc-100">{(r.data||{}).name}</td>
                <td className="p-3 text-zinc-400 text-xs">{(r.data||{}).type || "—"}</td>
                <td className="p-3"><a className="text-blue-400 hover:text-blue-300 text-xs" href={(r.data||{}).url} target="_blank" rel="noreferrer">Download</a></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={3} className="p-8 text-center text-zinc-500">No data</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
export function ClientPortalReports() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/records/reports").then(({ data }) => setItems(data)); }, []);
  return (
    <DashboardLayout title="Reports" subtitle="Monthly reports and analytics">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="portal-reports-grid">
        {items.map((r) => (
          <div key={r.record_id} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
            <div className="text-sm text-zinc-100">{(r.data||{}).title || "Report"}</div>
            <div className="text-xs text-zinc-500 mt-1">{(r.data||{}).period || ""}</div>
            <a className="mt-2 inline-block text-xs text-blue-400 hover:text-blue-300" href={(r.data||{}).url} target="_blank" rel="noreferrer">Open</a>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full p-8 text-center text-zinc-500 border border-dashed border-zinc-800 rounded-xl text-sm">No reports yet</div>}
      </div>
    </DashboardLayout>
  );
}
export function ClientPortalInvoices() { return <Invoices portal />; }
export function ClientPortalSupport() { return <Support portal />; }
export function ClientPortalAI() { return <AIAssistant />; }
